/* eslint-disable max-lines */
import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import classNames from 'clsx';
import { ReactComponent as FullscreenIconOut } from 'img/fullscreen_active.svg';
import { ReactComponent as ShapeIcon } from 'img/icons/shape.svg';
import { ReactComponent as CloseIcon } from 'mrg-icons/src/mailru/actions/close_big.svg';
import { ReactComponent as FullscreenIconIn } from 'mrg-icons/src/mailru/navigation/zoom-overlay.svg';
import React, { Component, createRef, ReactElement } from 'react';
import { canCloneWeblink, getIsCloudAvailabe } from 'reactApp/appHelpers/appHelpers';
import { IS_MY_TEAM, IS_ONPREMISE, IS_PUBLIC_FOLDER } from 'reactApp/appHelpers/configHelpers';
import { AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { IStoryFileItem } from 'reactApp/modules/stories/stories.types';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { EColor, FavoriteIcon } from 'reactApp/ui/FavoriteIcon/FavoriteIcon';
import { Logo } from 'reactApp/ui/Header/Logo/Logo';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { ProtectedHint } from 'reactApp/ui/ProtectedHint/ProtectedHint';
import { AttachInfo } from 'reactApp/ui/ReactViewer/AttachInfo/AttachInfo';
import { FaceInfoTooltip } from 'reactApp/ui/ReactViewer/ViewerHeader/FaceInfoTooltip/FaceInfoTooltip';
import { RegisterCloudTooltip } from 'reactApp/ui/RegisterCloudTooltip/RegisterCloudTooltip';
import {
    CopyIcon,
    DownloadIcon,
    ExternalLinkIcon,
    LinkIcon,
    MailIcon,
    MoreHorizontalIcon,
    MoveIcon,
    TrashbinIcon,
    UploadIcon,
} from 'reactApp/ui/VKUIIcons';
import { noop } from 'reactApp/utils/helpers';
import { getSize } from 'reactApp/utils/sizeHelper';

import styles from './ViewerHeader.css';
import { ViewerHeaderButton } from './ViewerHeaderButton';

interface Props {
    enableFavorites: boolean;
    isPublic: boolean;
    isStock: boolean;
    isAttaches?: boolean;
    isAuthorized: boolean;
    isArchiveItem?: boolean;
    id?: string;
    item: CloudFile | AttachesItem | IStoryFileItem | null;
    itemStorage?: EStorageType | null;
    toggleFavorite();
    onClose();
    onDownload(unknown);
    onToggleFavorites(item: CloudFile | AttachesItem | IStoryFileItem | null);
    onPublish(unknown);
    onDelete(item: CloudFile | AttachesItem | IStoryFileItem | null);
    onCopy();
    onMove();
    onMail();
    onOpenInMyCloud();
    onClone();
    onOpenParent();
    onActive({ isActive: boolean });
    onFileClick(): void;
    isFullScreen: boolean;
    showLogo?: boolean;
    embedded?: boolean;
    onFullscreen: () => void;
    isB2BBizUser?: boolean;
    isAttachesEnabled?: boolean;
    renderTopChildren?(isActive: boolean);
    topTitle?: string;
    topSubTitle?: string;
    isUnpublished?: boolean;
    facesCount?: number;
    onShowFaceFilter?();
    showFacesFilter?: boolean;
    disableClose?: boolean;
}

interface State {
    isMoreOpen: boolean;
    isDownloadOpen: boolean;
}

const getLinkText = 'Получить ссылку';
const unpublishLinkText = 'Отключить ссылку';
const shareLink = 'Поделиться ссылкой';
const deleteFromFavorites = 'Убрать из избранного';
const addToFavorites = 'Добавить в избранное';

export class ViewerHeader extends Component<Props, State> {
    static defaultProps = {
        enableFavorites: false,
        isPublic: false,
        isStock: false,
        isAttaches: false,
        isAuthorized: false,
        isArchiveItem: false,
        embedded: false,
        showLogo: false,
        item: null,
        isAttachesEnabled: true,
        id: '',
        onClose: noop,
        toggleFavorite: noop,
        onDownload: noop,
        onToggleFavorites: noop,
        onPublish: noop,
        onDelete: noop,
        onActive: noop,
        onFullscreen: noop,
        onFileClick: noop,
        onOpenInMyCloud: noop,
        renderTopChildren: noop,
        onShowFaceFilter: noop,
        isFullScreen: false,
        facesCount: 0,
        showFacesFilter: false,
        disableClose: false,
    };

    state = { isMoreOpen: false, isDownloadOpen: false };

    private buttonTextRef = createRef<HTMLDivElement>();
    private moreRef = createRef<HTMLDivElement>();
    private downloadRef = createRef<HTMLDivElement>();
    private faceCounterRef = createRef<HTMLDivElement>();
    private cloneRef = createRef<HTMLDivElement>();

    private calcDropdownPosition =
        (ref, xDisplacement = 0) =>
        () => {
            let posX = 0;
            let posY = 0;
            let maxHeight;

            if (ref.current) {
                const elRect = ref.current.getBoundingClientRect();

                posY = elRect.top + elRect.height + window.scrollY + 4;
                posX = elRect.left + xDisplacement;
                maxHeight = `${window.innerHeight - (posY - window.scrollY) - 8}px`;
            }

            return { posY, posX, maxHeight };
        };

    private handlePublish = (e): void => {
        const { item, onPublish } = this.props;

        onPublish({ item, textRef: this.buttonTextRef, event: e });
    };

    private handleDownload = (): void => {
        const { item, onDownload } = this.props;

        onDownload(item);
    };

    private handleOnToggleFavorites = (): void => {
        const { item, onToggleFavorites } = this.props;

        onToggleFavorites(item);
    };

    private handleDelete = () => {
        const { item, onDelete } = this.props;

        onDelete(item);
    };

    private handleClose = () => {
        const { onClose, item } = this.props;

        if (item?.weblinkDownloadable) {
            return open('/', '_self');
        }

        onClose();
    };

    private handleCloseMore = () => {
        this.props.onActive({ isActive: false });
        this.setState({ isMoreOpen: false });
    };

    private handleMoreClick = () => {
        this.setState((state) => {
            const newValue = !state.isMoreOpen;
            this.props.onActive({ isActive: newValue });
            return { isMoreOpen: newValue };
        });
    };

    private handleDownloadDropdownClick = () => {
        this.setState((state) => {
            const newValue = !state.isDownloadOpen;
            this.props.onActive({ isActive: newValue });
            return { isDownloadOpen: newValue };
        });
        this.props.onActive({ isActive: true });
    };

    private handleCloseDownload = () => {
        this.setState({ isDownloadOpen: false });
        this.props.onActive({ isActive: false });
    };

    private renderDropdownItem = (item) => {
        return <DropdownItemAction active={false} text={item.text} icon={item.icon} />;
    };

    private renderDownload = (item) => {
        const allowDownloadAsJpeg = downloadAsJpeg.isAvailable({ ext: item.ext, itemType: item.type, storage: item.storage });
        const { isDownloadOpen } = this.state;
        const { isPublic, isAttaches, isArchiveItem, isUnpublished } = this.props;
        // достаточно проверки только на флаг DOWNLOADABLE, т.к. если счетчик кончается, веблинк удаляется
        const showDownloadBtn = IS_ONPREMISE ? item.weblinkDownloadable || item.weblinkAccessRights !== 'r' : true;

        if ((isArchiveItem && (item.isFolder || item.isEncrypted)) || isUnpublished) {
            return null;
        }

        return (
            <div ref={this.downloadRef}>
                {showDownloadBtn && (
                    <ViewerHeaderButton
                        title={item.isFolder ? 'Скачать' : 'Скачать файл'}
                        icon={<DownloadIcon />}
                        primary={isPublic && !isAttaches && !item.weblinkDownloadable}
                        onClick={this.handleDownload}
                        onClickDropdown={this.handleDownloadDropdownClick}
                        isDropdownOpen={isDownloadOpen}
                        showDropDown={allowDownloadAsJpeg}
                        id="download"
                    />
                )}
                {isDownloadOpen && (
                    <DropdownList
                        list={downloadAsJpeg.getDropdownItems({
                            itemOrId: item,
                            gaSuffix: 'viewer',
                            ext: item.ext,
                        })}
                        renderItem={this.renderDropdownItem}
                        close={this.handleCloseDownload}
                        posX={0}
                        posY={0}
                        theme={[DropdownTheme.jpeg, DropdownTheme.dark]}
                        parentRef={this.downloadRef}
                        calcPosition={this.calcDropdownPosition(this.downloadRef, 58)}
                        closeOnResize
                        closeOnScroll
                        title="Скачать в"
                        gaId="viewer-dnld"
                    />
                )}
            </div>
        );
    };

    private getMoreItems = (isStory = false) => {
        const { isAttachesEnabled } = this.props;

        const moreItems = isStory
            ? [
                  {
                      id: 'download',
                      text: 'Скачать',
                      onClick: () => this.handleDownload(),
                      icon: <DownloadIcon />,
                  },
                  {
                      id: 'openinparent',
                      text: 'Показать в папке',
                      onClick: this.props.onOpenParent,
                      icon: <ExternalLinkIcon />,
                  },
                  {
                      id: 'copy',
                      text: 'Копировать',
                      onClick: this.props.onCopy,
                      icon: <CopyIcon />,
                  },
                  {
                      id: 'move',
                      text: 'Переместить в папку',
                      onClick: this.props.onMove,
                      icon: <MoveIcon />,
                  },
                  {
                      id: 'delete',
                      text: 'Удалить',
                      onClick: () => this.handleDelete(),
                      icon: <TrashbinIcon />,
                  },
              ]
            : [
                  {
                      id: 'openinparent',
                      text: 'Показать в папке',
                      onClick: this.props.onOpenParent,
                      icon: <ExternalLinkIcon />,
                  },
                  {
                      id: 'copy',
                      text: 'Копировать',
                      onClick: this.props.onCopy,
                      icon: <CopyIcon />,
                  },
                  {
                      id: 'move',
                      text: 'Переместить в папку',
                      onClick: this.props.onMove,
                      icon: <MoveIcon />,
                  },
              ];

        if (isAttachesEnabled && !IS_MY_TEAM) {
            moreItems.push({
                id: 'mail',
                text: 'Отправить по почте',
                onClick: this.props.onMail,
                icon: <MailIcon />,
            });
        }

        return moreItems;
    };

    private renderPrivateToolbarItems = () => {
        const { item, enableFavorites, isArchiveItem } = this.props;
        const { isMoreOpen } = this.state;

        const isInFavorites = Boolean(item && 'isInFavorites' in item && item.isInFavorites);
        const isPublished = Boolean(item && 'weblink' in item && item.weblink);

        const downloadBttn = this.renderDownload(item);

        if (isArchiveItem) {
            return downloadBttn;
        }

        return (
            <>
                <ViewerHeaderButton
                    title={isPublished ? shareLink : getLinkText}
                    icon={<LinkIcon />}
                    onClick={this.handlePublish}
                    textRef={this.buttonTextRef}
                    id="publish"
                    primary
                />
                {downloadBttn}
                {enableFavorites && (
                    <ViewerHeaderButton
                        title={<div className={styles.button_fav}>{isInFavorites ? deleteFromFavorites : addToFavorites}</div>}
                        icon={<FavoriteIcon className={styles.favoritesIcon} active={isInFavorites} color={EColor.WHITE} />}
                        onClick={this.handleOnToggleFavorites}
                        id="favorite"
                    />
                )}
                <div className={styles.iconsGroup}>
                    <ViewerHeaderButton
                        title=""
                        icon={<TrashbinIcon />}
                        onClick={this.handleDelete}
                        hint="Удалить файл"
                        isFilled={false}
                        id="delete"
                    />
                    <div ref={this.moreRef}>
                        <ViewerHeaderButton
                            title=""
                            icon={<MoreHorizontalIcon />}
                            onClick={this.handleMoreClick}
                            hint="Дополнительные действия"
                            id="more"
                        />
                        {isMoreOpen && (
                            <DropdownList
                                list={this.getMoreItems()}
                                renderItem={this.renderDropdownItem}
                                close={this.handleCloseMore}
                                theme={DropdownTheme.dark}
                                parentRef={this.moreRef}
                                calcPosition={this.calcDropdownPosition(this.moreRef, 2)}
                                closeOnResize
                                gaId="viewer-more"
                            />
                        )}
                    </div>
                </div>
            </>
        );
    };

    private renderStoryToolbarItems = () => {
        const { item, enableFavorites } = this.props;
        const { isMoreOpen } = this.state;

        const isInFavorites = Boolean(item && 'isInFavorites' in item && item.isInFavorites);
        const isPublished = Boolean(item && 'weblink' in item && item.weblink);

        return (
            <>
                <ViewerHeaderButton
                    title={isPublished ? shareLink : getLinkText}
                    hint={isPublished ? shareLink : getLinkText}
                    icon={<LinkIcon />}
                    onClick={this.handlePublish}
                    textRef={this.buttonTextRef}
                    id="publish"
                    primary
                />
                {enableFavorites && (
                    <ViewerHeaderButton
                        title={<div className={styles.button_fav}>{isInFavorites ? deleteFromFavorites : addToFavorites}</div>}
                        hint={isInFavorites ? deleteFromFavorites : addToFavorites}
                        icon={<FavoriteIcon className={styles.favoritesIcon} active={isInFavorites} color={EColor.WHITE} />}
                        onClick={this.handleOnToggleFavorites}
                        id="favorite"
                    />
                )}
                <div className={styles.iconsGroup}>
                    <div ref={this.moreRef}>
                        <ViewerHeaderButton
                            title=""
                            icon={<MoreHorizontalIcon />}
                            onClick={this.handleMoreClick}
                            hint="Дополнительные действия"
                            id="more"
                        />
                        {isMoreOpen && (
                            <DropdownList
                                list={this.getMoreItems(true)}
                                renderItem={this.renderDropdownItem}
                                close={this.handleCloseMore}
                                posX={0}
                                posY={0}
                                theme={DropdownTheme.dark}
                                parentRef={this.moreRef}
                                calcPosition={this.calcDropdownPosition(this.moreRef, 2)}
                                closeOnResize
                                gaId="viewer-more"
                            />
                        )}
                    </div>
                </div>
            </>
        );
    };

    private renderStockToolbarItems = () => {
        const { item, isAuthorized, isArchiveItem, onClone, itemStorage } = this.props;

        const showCloneExcludeArchive = Boolean(canCloneWeblink(isAuthorized, itemStorage, true)) && !isArchiveItem;

        return (
            <>
                <>
                    {!isArchiveItem && (
                        <ViewerHeaderButton
                            title={shareLink}
                            hint={shareLink}
                            icon={<LinkIcon />}
                            onClick={this.handlePublish}
                            textRef={this.buttonTextRef}
                            id="publish"
                            primary
                        />
                    )}
                    {showCloneExcludeArchive && (
                        <ViewerHeaderButton
                            title="Сохранить в Облако"
                            hint="Сохранить в Облако"
                            icon={<UploadIcon />}
                            onClick={onClone}
                            id="clone"
                            collapsableText
                        />
                    )}
                </>
                {this.renderDownload(item)}
            </>
        );
    };

    private renderPublicToolbarItems = (showSendViaMail: boolean, showLink: boolean) => {
        const {
            item,
            isAuthorized,
            isArchiveItem,
            embedded,
            isFullScreen,
            onFullscreen,
            isAttachesEnabled,
            isUnpublished,
            facesCount = 0,
            onShowFaceFilter,
            itemStorage,
            isStock,
        } = this.props;
        let isTempAttachType = false;
        if (item && 'attachType' in item) {
            isTempAttachType = item.attachType === EAttachTypes.temporary;
        }
        const showClone = !item?.weblinkDownloadable && Boolean(canCloneWeblink(isAuthorized, itemStorage, true)) && !isTempAttachType;
        const showOpenInCloud = !showClone && !item?.weblinkDownloadable && embedded && getIsCloudAvailabe();
        const showCopyLink = canCloneWeblink(isAuthorized, itemStorage);

        const showCloneExcludeArchive = showClone && !isArchiveItem;

        return (
            <>
                {this.renderDownload(item)}
                {showCloneExcludeArchive && (
                    <>
                        <ViewerHeaderButton
                            title="Сохранить в Облако"
                            hint="Сохранить в Облако"
                            icon={<UploadIcon />}
                            onClick={this.props.onClone}
                            id="clone"
                            collapsableText
                            textRef={this.cloneRef}
                        />
                        {/* Временно выключил на сток-файлах. Включить после перевода стоков на новый SSR - CLOUDWEB-14426 */}
                        {!isStock && <RegisterCloudTooltip targetRef={this.cloneRef} closeOnScroll={true} />}
                    </>
                )}
                {showOpenInCloud && (
                    <ViewerHeaderButton
                        title="Открыть в моём Облаке"
                        hint="Открыть в моём Облаке"
                        icon={<ExternalLinkIcon />}
                        onClick={this.props.onOpenInMyCloud}
                        id="openinmy"
                        collapsableText
                    />
                )}
                {isAttachesEnabled && showSendViaMail && !isArchiveItem && (
                    <ViewerHeaderButton
                        title="Отправить по почте"
                        hint="Отправить по почте"
                        icon={<MailIcon data-qa-id="mail" />}
                        onClick={this.props.onMail}
                        id="mail"
                        collapsableText
                    />
                )}
                {showLink && !item?.weblinkDownloadable && !isArchiveItem && !isUnpublished && (
                    <ViewerHeaderButton
                        title={showCopyLink ? getLinkText : unpublishLinkText}
                        hint={showCopyLink ? getLinkText : unpublishLinkText}
                        icon={<LinkIcon />}
                        onClick={this.handlePublish}
                        textRef={this.buttonTextRef}
                        id="publish"
                        collapsableText
                    />
                )}
                {facesCount > 0 && (
                    <>
                        <ViewerHeaderButton
                            title=""
                            hint="Показать фильтр по лицам"
                            textOnIcon={facesCount?.toString()}
                            icon={<ShapeIcon />}
                            onClick={onShowFaceFilter}
                            textRef={this.faceCounterRef}
                            id="showFaces"
                            iconOnly
                        />
                        {Boolean(this.faceCounterRef?.current) && (
                            <FaceInfoTooltip onApply={onShowFaceFilter} targetRef={this.faceCounterRef} />
                        )}
                    </>
                )}
                {embedded && (
                    <ViewerHeaderButton
                        title=""
                        icon={isFullScreen ? <FullscreenIconOut width={16} height={16} /> : <FullscreenIconIn width={16} height={16} />}
                        onClick={onFullscreen}
                        hint={isFullScreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}
                        id="fullscreen"
                    />
                )}
                {item?.weblinkDownloadable && (
                    <Hint text="Закрыть" theme="dark" showDelay>
                        <div className={styles.closeIcon} onClick={this.handleClose}>
                            <CloseIcon />
                        </div>
                    </Hint>
                )}
            </>
        );
    };

    private renderAttachToolbarItems = () => {
        const { item, isAuthorized, isArchiveItem, itemStorage } = this.props;
        const showClone = Boolean(canCloneWeblink(isAuthorized, itemStorage));
        const attachType = (item as AttachesItem)?.attachType;
        const isTemporary = attachType === EAttachTypes.temporary;
        const isCloudAttach = attachType === EAttachTypes.cloud;

        // eslint-disable-next-line max-lines
        return (
            <>
                {showClone && !isArchiveItem && !isTemporary && !isCloudAttach && (
                    <>
                        <ViewerHeaderButton
                            title={shareLink}
                            hint={shareLink}
                            icon={<LinkIcon />}
                            onClick={this.handlePublish}
                            textRef={this.buttonTextRef}
                            id="publish"
                            primary
                        />
                        <ViewerHeaderButton
                            title="Сохранить в Облако"
                            hint="Сохранить в Облако"
                            icon={<UploadIcon />}
                            onClick={this.props.onClone}
                            id="clone"
                            collapsableText
                        />
                    </>
                )}
                {this.renderDownload(item)}
            </>
        );
    };

    private renderAlbumsToolbarItems = () => {
        const { item } = this.props;

        const isInFavorites = Boolean(item && 'isInFavorites' in item && item.isInFavorites);

        const downloadBttn = this.renderDownload(item);

        return (
            <>
                {downloadBttn}
                <ViewerHeaderButton
                    title={<div className={styles.button_fav}>{isInFavorites ? deleteFromFavorites : addToFavorites}</div>}
                    icon={<FavoriteIcon className={styles.favoritesIcon} active={isInFavorites} color={EColor.WHITE} />}
                    onClick={this.handleOnToggleFavorites}
                    id="favorite"
                    iconOnly
                />
                <div className={styles.iconsGroup}>
                    <ViewerHeaderButton
                        title="Удалить"
                        icon={<TrashbinIcon />}
                        onClick={this.handleDelete}
                        hint="Удалить файл"
                        isFilled={false}
                        id="delete"
                        iconOnly
                    />
                </div>
            </>
        );
    };

    private renderToolbar = () => {
        const { isPublic, isStock, isAttaches, embedded, item } = this.props;

        if (item?.storage === EStorageType.albums) {
            return this.renderAlbumsToolbarItems();
        }

        if (isAttaches) {
            return this.renderAttachToolbarItems();
        }

        if (isStock) {
            return this.renderStockToolbarItems();
        }

        if (isPublic) {
            return this.renderPublicToolbarItems(!embedded, !!embedded);
        }

        if (item?.storage === EStorageType.story) {
            return this.renderStoryToolbarItems();
        }

        return this.renderPrivateToolbarItems();
    };

    render(): ReactElement | null {
        const {
            item,
            isFullScreen,
            onFullscreen,
            onFileClick,
            embedded,
            showLogo,
            renderTopChildren,
            topTitle,
            topSubTitle,
            showFacesFilter,
            disableClose,
        } = this.props;
        const { isDownloadOpen, isMoreOpen } = this.state;

        if (!item) {
            return null;
        }

        let topTitleBlock;
        if (item.storage === EStorageType.story) {
            topTitleBlock = (
                <div className={classNames(styles.fileInfo, styles.fileInfo_story)}>
                    <div className={styles.fileName}>
                        <NameComponent name={topTitle ?? ''} extension="" truncate showTooltip fullWidth />
                    </div>
                    <div className={styles.fileSize}>{topSubTitle}</div>
                </div>
            );
        } else {
            const name = item.isFolder ? item.name : item?.nameWithoutExt || '';

            topTitleBlock = (
                <div className={styles.fileInfo}>
                    <div className={styles.fileName}>
                        <NameComponent
                            name={name}
                            extension={item?.ext}
                            truncate
                            delayTruncate={embedded}
                            showTooltip
                            showUnknownExtension
                        />
                        {[EStorageType.public, EStorageType.stock].includes(item.storage) && !IS_PUBLIC_FOLDER && (
                            <ProtectedHint text="Файл проверен антивирусом Касперского" />
                        )}
                    </div>
                    {typeof item.size === 'number' ? <div className={styles.fileSize}>{getSize(item.size)}</div> : null}
                </div>
            );
        }

        return (
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_attaches]: item?.storage === EStorageType.attaches || item?.storage === EStorageType.viewerAttaches,
                    [styles.root_embedded]: embedded,
                    [styles.root_story]: item?.storage === EStorageType.story,
                })}
            >
                {renderTopChildren && renderTopChildren(isDownloadOpen || isMoreOpen)}
                <div className={styles.controlsWrapper}>
                    <div
                        className={classNames({
                            [styles.group]: true,
                            [styles.leftGroup]: true,
                            [styles.leftGroup_small]: Boolean(showFacesFilter),
                        })}
                    >
                        {item?.storage === EStorageType.attaches || item?.storage === EStorageType.viewerAttaches ? (
                            <AttachInfo
                                name={item?.subject}
                                author={item?.authorName}
                                timestamp={item?.mtime}
                                fileName={item?.nameWithoutExt}
                                fileExt={item?.ext}
                                size={getSize(item?.size || 0)}
                                isTemporary={item?.attachType === EAttachTypes.temporary}
                                onClick={onFileClick}
                            />
                        ) : (
                            <>
                                {showLogo && (
                                    <div className={styles.logo}>
                                        <Logo white />
                                    </div>
                                )}
                                {topTitleBlock}
                            </>
                        )}
                        {/* <div className={styles.fileInfoIcon} onClick={noop}>*/}
                        {/*    <InfoIcon />*/}
                        {/* </div>*/}
                    </div>
                    <div className={classNames(styles.group, styles.middleGroup)}>{this.renderToolbar()}</div>
                    {!embedded && (
                        <div className={classNames(styles.group, styles.rightGroup)}>
                            <ViewerHeaderButton
                                title=""
                                icon={
                                    isFullScreen ? (
                                        <FullscreenIconOut width={16} height={16} />
                                    ) : (
                                        <FullscreenIconIn width={16} height={16} />
                                    )
                                }
                                onClick={onFullscreen}
                                hint={isFullScreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}
                                id="fullscreen"
                            />
                            {!disableClose && (
                                <Hint text="Закрыть" theme="dark" showDelay>
                                    <div className={styles.closeIcon} onMouseDown={this.handleClose}>
                                        <CloseIcon />
                                    </div>
                                </Hint>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
